import { isEqual, isObject, memoize } from 'lodash-es';

import { debouncePromise } from '@iconik-io/core/src/debounce_promise';
import { parsePositiveInteger } from '@iconik-io/core/src/parse_positive_integer';
import type { EndpointId } from '@iconik-io/iconik-api/src/client/request';
import { EndpointError } from '@iconik-io/iconik-api/src/client/response';
import { getCommonFiltersSlice } from '@iconik-io/shared-lib/src/redux/common_filters/slice';
import type { FacetValues } from '@iconik-io/shared-lib/src/redux/common_filters/types';
import { isCancellation } from '@iconik-io/shared-lib/src/redux/middlewares/http/actions';
import type { EndpointResponseNormalized } from '@iconik-io/shared-lib/src/redux/middlewares/http_normalizer/types';
import { handleErrors } from '@iconik-io/shared-lib/src/utils/handle_messages';
import type { SortOption } from '@iconik-io/shared-lib/src/utils/sort/types';

import { INPUT_DEBOUNCE_TIMEOUT } from '../../constants/debounce';
import type { AppDispatch } from '../../store/types';
import type { FacetSchema, FacetsSchema } from '../../types/common';
import { httpCancelRequests } from '../http/actions';
import { commonListActions } from './slice';
import type { CommonListPrefix } from './types';

export type CommonListGetParams = {
  sort?: SortOption[];
  page?: number;
  per_page?: number;
  initialFacets?: FacetsSchema;
  initialFacetsValues?: FacetValues;
};

export type CommonListGetOptions<T, P extends CommonListGetParams> = {
  prefix: CommonListPrefix;
  params: P;
  dispatch: AppDispatch;
  getListPromise: (params: P) => Promise<T>;
  debounce?: boolean;
  // Debounce dependencies allow to specify which requests should be triggered
  // cancelled if there are any in progress and debounce queue is starting.
  debounceDependencies?: EndpointId[];
  pagination?: 'infinite' | 'regular';
};

// Memoization of debounced function by CommonListPrefix key is necessary for wrapCommonGetList
// as it handles creation of only one instance of a debounced function per key.
// Otherwise, we would get a new debounced function every wrapCommonGetList call,
// and such debouncing wouldn't make sense.
const memoizedDebounced = memoize(
  <T extends EndpointResponseNormalized, P extends CommonListGetParams>(
    _prefix: CommonListPrefix,
    fn: (params: P) => Promise<T>,
  ) => debouncePromise(fn, INPUT_DEBOUNCE_TIMEOUT),
);

const debounceProcessing: { [prefix in CommonListPrefix]?: boolean } = {};

export const wrapCommonGetList = <
  T extends EndpointResponseNormalized,
  P extends CommonListGetParams,
>({
  prefix,
  dispatch,
  params,
  debounce,
  debounceDependencies,
  pagination = 'infinite',
  getListPromise,
}: CommonListGetOptions<T, P>): Promise<T> => {
  const actions = commonListActions(prefix);
  const filtersActions = getCommonFiltersSlice(prefix).actions;
  if (params.sort) {
    dispatch(actions.setSort(params.sort));
  }
  dispatch(actions.setLoading(true));

  // Reset pagination on a fresh fetch (a debounced search or an explicit page 1)
  // so a stale infinite-scroll handler cannot request a page that only existed
  // for the previous query. Ids are left intact to avoid flicker; the page 1
  // response replaces them.
  const isFreshFetch =
    pagination !== 'regular' &&
    (debounce === true || params.page === undefined || params.page === 1);
  if (isFreshFetch) {
    dispatch(actions.resetPagination());
  }

  let shouldDebounce = debounce;
  // Debounced list fetching normally is triggered by continuous user's input,
  // we don't want to mess up such calls with non-debounced ones, so we are
  // forcing them to be debounced ones as well.
  if (!debounce && debounceProcessing[prefix]) {
    shouldDebounce = true;
  }

  if (shouldDebounce) {
    debounceProcessing[prefix] = true;
    // Cancel all dependent requests that might be in progress.
    if (debounceDependencies) {
      dispatch(httpCancelRequests(debounceDependencies));
    }
  }
  let cancelled = false;
  return (
    shouldDebounce
      ? memoizedDebounced(prefix, getListPromise)(params)
      : getListPromise(params)
  )
    .then((result: T) => {
      const { body, meta } = result;
      if (!meta?.normalizedMultiple) {
        return result;
      }
      if (!isObject(body)) {
        return result;
      }
      if (('page' in body && body.page === 1) || pagination === 'regular') {
        dispatch(actions.setIds(meta.normalizedMultiple.result));
      } else {
        dispatch(actions.appendIds(meta.normalizedMultiple.result));
      }

      if ('facets' in body && body.facets) {
        // biome-ignore lint/plugin: aggregations are generated as a bag of unknowns; the keys read below are the ones the list asked for.
        const facets = body.facets as FacetsSchema;
        const initialFacetValuesKeys = Object.keys(
          params?.initialFacetsValues || {},
        );

        const initialBuckets = Object.keys(params.initialFacets || {})
          .filter((item) => initialFacetValuesKeys.includes(item))
          .map((item) => params.initialFacets?.[item].buckets);
        const newBuckets = Object.keys(facets || {})
          .filter((item) => initialFacetValuesKeys.includes(item))
          .map((item) => facets?.[item].buckets);

        let currentFacets: FacetsSchema['current_facet_filters'] | undefined;
        if ('current_facet_filters' in facets && facets.current_facet_filters) {
          if (!isEqual(newBuckets, initialBuckets)) {
            const cachedFacetValues: {
              [key: string]: FacetSchema;
            } = {};
            initialFacetValuesKeys.forEach((key) => {
              cachedFacetValues[key] = {
                ...facets[key],
                buckets: params.initialFacets?.[key]?.buckets,
              };
            });

            currentFacets = {
              ...facets.current_facet_filters,
              ...cachedFacetValues,
            };
          } else {
            currentFacets = facets.current_facet_filters;
          }
        }

        dispatch(
          filtersActions.setAvailableFacets({ ...facets, ...currentFacets }),
        );
      }
      dispatch(
        actions.setPagination({
          page: 'page' in body ? (parsePositiveInteger(body.page) ?? 1) : 1,
          pages: 'pages' in body ? (parsePositiveInteger(body.pages) ?? 1) : 1,
          total: 'total' in body && body.total ? Number(body.total) : 0,
          per_page:
            'per_page' in body && body.per_page ? Number(body.per_page) : 0,
        }),
      );

      return result;
    })
    .catch((error) => {
      if (isCancellation(error)) {
        cancelled = true;
        throw error;
      }
      if (error instanceof EndpointError) {
        const errors = handleErrors(error);
        dispatch(
          actions.setLatestError({
            status: error.response.status,
            statusText: error.response.statusText,
            headers: error.response.headers,
            errors,
          }),
        );
      }
      throw error;
    })
    .finally(() => {
      // A cancelled request was superseded by a newer one that now owns the
      // loading state, so leave it untouched to avoid a reload loop.
      if (cancelled) {
        return;
      }
      debounceProcessing[prefix] = false;
      dispatch(actions.setLoading(false));
    });
};

export type CommonListCreateOptions<T> = {
  dispatch: AppDispatch;
  prefix: CommonListPrefix;
  insertAt?: 'start' | 'end';
  createPromise: Promise<T>;
};

export const wrapCommonCreate = <T extends EndpointResponseNormalized>({
  prefix,
  dispatch,
  insertAt = 'start',
  createPromise,
}: CommonListCreateOptions<T>): Promise<T> =>
  createPromise.then((result) => {
    const { meta } = result;
    if (!meta?.normalizedSingular) {
      return result;
    }
    const actions = commonListActions(prefix);
    switch (insertAt) {
      case 'start':
        dispatch(actions.prependId(meta.normalizedSingular.result));
        break;
      case 'end':
        dispatch(actions.appendId(meta.normalizedSingular.result));
        break;
      default:
        break;
    }

    return result;
  });

export type CommonListDeleteOptions = {
  dispatch: AppDispatch;
  prefix: CommonListPrefix;
  ids: string[];
  deletePromise: Promise<any>;
};

export const wrapCommonDelete = ({
  prefix,
  dispatch,
  ids,
  deletePromise,
}: CommonListDeleteOptions): Promise<void> =>
  deletePromise.then(() => {
    const actions = commonListActions(prefix);

    dispatch(actions.deleteIds(ids));
  });
